import { type FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';

import { type ResponseBoard, type SAQuestion, ElementType } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type RootState } from '@editor2/reducers/root';
import ConfigCard from '../Question/ConfigCard';

interface Props {
	element: SAQuestion | ResponseBoard;
	courseId: number;
	pageType: string;
}

const QuestionConfig: FC<Props> = (props) => {
	const { element, courseId, pageType } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const dispatch = useDispatch();

	const { page_types, element_types } = useSelector(
		(state: RootState) => state.courses[courseId].config
	);

	const elementType = Object.keys(ElementType).find((key) => ElementType[key] === element.type);

	const tocPageEditable = page_types[pageType].elements[elementType].editable;
	const tocElementEditable = element_types[elementType].editable;

	const isNullOrUndefined = (value) => value === null || value === undefined;
	const isEditableSetOnQuestion = !isNullOrUndefined(element.editable);

	const isEditable = (element) => {
		if (!isNullOrUndefined(element.editable)) {
			return element.editable;
		}
		if (!isNullOrUndefined(tocPageEditable)) {
			return tocPageEditable;
		}
		if (!isNullOrUndefined(tocElementEditable)) {
			return tocElementEditable;
		}
		return false;
	};

	const tocValue = () => {
		if (!isNullOrUndefined(tocPageEditable)) {
			return { source: 'page-element configuration', value: tocPageEditable };
		}
		if (!isNullOrUndefined(tocElementEditable)) {
			return { source: 'element configuration', value: tocElementEditable };
		}
		return {
			source: '',
			value: false
		};
	};

	const [overrideEditable, setOverrideEditable] = useState(isEditableSetOnQuestion);
	const [editableEnabled, setEditableEnabled] = useState(isEditable(element));

	const setEditable = (value: boolean) => {
		dispatch(
			updateElement({
				...commonPayload,
				editable: value
			})
		);
	};

	useEffect(() => {
		// for multiple tabs update

		if (!isNullOrUndefined(element.editable) !== overrideEditable) {
			setOverrideEditable(!isNullOrUndefined(element.editable));
		}

		if (isEditable(element) !== editableEnabled) {
			setEditableEnabled(isEditable(element));
		}
	}, [element]);

	useEffect(() => {
		if (!isNullOrUndefined(element.editable) && !overrideEditable) {
			setEditable(null);
			setEditableEnabled(tocValue().value);
		}
	}, [overrideEditable]);

	useEffect(() => {
		if (overrideEditable && editableEnabled !== element.editable) {
			setEditable(editableEnabled);
		}
	}, [editableEnabled]);

	const getDefaultValue = () => {
		const source = tocValue().source ? ` from ${tocValue().source}` : '';
		return `- (Default${source})`;
	};

	return (
		<ConfigCard
			title="Allow students to edit answer"
			enabled={editableEnabled}
			overrideTitle={`Override ${tocValue().source}`}
			override={overrideEditable}
			setOverride={setOverrideEditable}>
			<Form.Group>
				<Form.Check
					id={element.family_id}
					type="radio"
					checked={editableEnabled}
					onChange={() => setEditableEnabled(!editableEnabled)}
					label={`Enable editability ${tocValue().value ? getDefaultValue() : ''}`}
					disabled={!overrideEditable}
					className="editabilityRadio"
				/>

				<Form.Check
					id={'element.family_id'}
					type="radio"
					checked={!editableEnabled}
					onChange={() => setEditableEnabled(!editableEnabled)}
					label={`Disable editability ${!tocValue().value ? getDefaultValue() : ''}`}
					disabled={!overrideEditable}
				/>
			</Form.Group>
		</ConfigCard>
	);
};

export default QuestionConfig;
